import React, { useEffect } from 'react'

import { BodyText, HeadingSmall, Label, HeadingXSmall } from '@pb/components'
import { isNorthstarBrand } from '@pb/utils'

import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import Sticky from 'react-stickynode'

import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import ClubCard from 'features/account/club-card'
import useAccountFeature from 'features/account/hooks/useAccountFeature'
import useOptimizelyDecision from 'features/account/hooks/useOptimizelyDecision'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import layoutStyles from 'features/account/layoutStyle.scss'
import NewMemberCard from 'features/account/new-member-card/NewMemberCard'
import NewMemberCardNS from 'features/account/new-member-card/NewMemberCardNS'
import { UserMenu } from 'features/account/user-menu'
import userMenuActions from 'features/account/user-menu/duck/actions'

import styles from './MyAccountLayout.scss'

export interface MyAccountLayoutProps {
  className?: string
  isDisabled: boolean
  hasExtraPadding?: boolean
  shouldShowNSLayout?: boolean
}
const MyAccountLayout: React.FC<MyAccountLayoutProps> = ({
  children,
  className,
  isDisabled,
  hasExtraPadding,
  shouldShowNSLayout,
}) => {
  const dispatch = useDispatch()
  const hasUserFailed = useSelector(appSelectors.getIsUserLoadingFailed)
  const userIsNonMember = useSelector(appSelectors.getIsUserOldAccountHolder)
  const userIsFullMember = useSelector(appSelectors.getUserIsFullMember)
  const userIsPendingMember = useSelector(appSelectors.getUserIsPendingMember)

  const userDisplayName = useSelector(appSelectors.getUserDisplayName)
  const userEmail = useSelector(appSelectors.getUserEmail)
  const inviteFriendPageEnabled = useSelector(appSelectors.getIsInviteFriendEnabled)

  const {
    pages: {
      hub: { shouldShowClubCard },
    },
    shouldShowFooter,
  } = useAccountFeature()

  const isNewMemberCardEnabled = useOptimizelyToggle({
    key: OPTIMIZELY.ACCOUNT_NEW_MEMBER_CARD,
  })

  const shouldShowNorthstarVariant = useOptimizelyDecision(OPTIMIZELY.ACCOUNT_NORTHSTAR_VARIATION)
  const showShouldNewMemberCardNS = shouldShowNorthstarVariant && isNewMemberCardEnabled

  useEffect(() => {
    if (inviteFriendPageEnabled && userIsFullMember) {
      dispatch(userMenuActions.addInviteFriendMenuItem())
    }
  }, [inviteFriendPageEnabled])

  return (
    <>
      {isDisabled ? (
        <div className={className}>{children}</div>
      ) : (
        <>
          {userIsFullMember &&
            shouldShowClubCard &&
            shouldShowNSLayout &&
            showShouldNewMemberCardNS && <NewMemberCardNS />}
          <div
            className={cn(
              layoutStyles.menuPlusContent,
              !shouldShowNorthstarVariant
                ? layoutStyles.menuPlusContentWidth
                : layoutStyles.hidePadding_right,
            )}
          >
            <Sticky bottomBoundary={shouldShowFooter ? '.site-footer-scroll-stop' : 'footer'}>
              <aside>
                {hasUserFailed && (
                  <header className={styles.heroMessage}>
                    <HeadingSmall>
                      <Label copyKey="hub.custumer.los.error" />
                    </HeadingSmall>
                  </header>
                )}
                {(userIsNonMember || userIsPendingMember) && (
                  <>
                    {userDisplayName && <HeadingXSmall Tag="p">{userDisplayName}</HeadingXSmall>}
                    <BodyText>{userEmail}</BodyText>
                  </>
                )}

                {userIsFullMember && shouldShowClubCard && (
                  <>
                    {isNorthstarBrand ? (
                      !shouldShowNSLayout &&
                      (shouldShowNorthstarVariant && isNewMemberCardEnabled ? (
                        <NewMemberCard />
                      ) : (
                        <ClubCard />
                      ))
                    ) : (
                      <>{isNewMemberCardEnabled ? <NewMemberCard /> : <ClubCard />}</>
                    )}
                  </>
                )}
                <UserMenu />
              </aside>
            </Sticky>
            <div
              className={cn(
                layoutStyles.container,
                hasExtraPadding && layoutStyles.hasExtraPadding,
                shouldShowNorthstarVariant && layoutStyles.extendedContainer,
              )}
            >
              {children}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default MyAccountLayout
